import {
  Box,
  Button,
  InputLabel,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC, useState } from "react";

import useResponsive from "../../hooks/useResponsive";
import { InputCustomSize } from "../UIElements/input-ui";
import { labelSty } from "../UIElements/styles-input";
import { useMutation } from "@tanstack/react-query";
import { apiAssignPermissionToRoles, apiCreateRoles } from "../../api/access";
import { useNotificationService } from "../notification";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { PATH_USER } from "../../routes/path";

// Define the types
type VerbData = {
  alias: string;
  descriptions: string;
  // Add other properties as needed for verb data
};

type SelectedVerbItem = {
  verb: string;
  description: string;
  verbData: VerbData;
};

type VerbDetail = {
  alias: string;
  descriptions: string;
};

export type EntryValue = {
  key: string;
  path: string;
  description: string;
  privilege: {
    verbs: Record<string, VerbDetail>; // Allows any string keys for verbs
  };
};

const PermissionsDetailsContent: FC<{
  stateData: { permissions: any[]; module: any };
  loading: boolean;
  filterData: { filter: string; module: string };
  setFilterData: React.Dispatch<
    React.SetStateAction<{
      filter: string;
      module: string;
    }>
  >;
}> = ({ stateData, filterData, setFilterData, loading }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMedium = useResponsive("down", "md", "xl");
  const { showNotification } = useNotificationService();

  const [selectedVerbData, setSelectedVerbData] = useState<EntryValue[]>([]);

  const [entryValue, setEntryValue] = useState<EntryValue | null>(null);
  const [data, setData] = useState({ name: "", description: "" });
  const [role_data, setRole_data] = useState({
    _id: "",
    show_permission: false,
  });

  // Generate a unique key for each verb and description combination
  const generateVerbKey = (verb: string, description: string) =>
    `${verb}-${description}`;

  const handleVerbClick = (
    verb: string,
    description: string,
    verbData: VerbDetail,
    entry: {
      path: string;
      descriptions: string;
      key: string;
    },
  ) => {
    // Create the new EntryValue item
    const newEntry: EntryValue = {
      key: entry.key,
      path: entry.path,
      description: entry.descriptions,
      privilege: {
        verbs: {
          [verb]: verbData,
        },
      },
    };

    // Update state with toggle logic
    setSelectedVerbData((prevState) => {
      const entryIndex = prevState.findIndex((item) => item.key === entry.key);

      if (entryIndex !== -1) {
        const existingEntry = prevState[entryIndex];
        const updatedVerbs = { ...existingEntry.privilege.verbs };

        // Toggle verb in verbs object
        if (updatedVerbs[verb]) {
          delete updatedVerbs[verb];
        } else {
          updatedVerbs[verb] = verbData;
        }

        // Create a new updated entry with toggled verbs
        const updatedEntry = {
          ...existingEntry,
          privilege: { verbs: updatedVerbs },
        };

        // Replace the old entry in the array with the updated one
        return [
          ...prevState.slice(0, entryIndex),
          updatedEntry,
          ...prevState.slice(entryIndex + 1),
        ].filter((item) => Object.keys(item.privilege.verbs).length > 0);
      } else {
        // If entry doesn’t exist, add new entry to array
        return [...prevState, newEntry];
      }
    });
  };

  const headerSty = {
    fontFamily: "Lexend",
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };

  const handleModule = (val: string) => {
    setFilterData((prev) => ({ ...prev, module: val.toLowerCase() }));
  };

  const role_mutation = useMutation({
    mutationFn: (field: { name: string; description: string }) =>
      apiCreateRoles(field),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Success");
        setRole_data({ _id: data.data?.data?._id, show_permission: true });
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  const handleCreateRole = () => {
    if (data.name === "") {
      showNotification("Name is required", "error");
      return;
    } else if (data.description === "") {
      showNotification("Description is required", "error");
      return;
    }
    role_mutation.mutate({
      name: data.name,
      description: data.description,
    });
  };
  const permission_mutation = useMutation({
    mutationFn: (field: EntryValue[]) =>
      apiAssignPermissionToRoles(field, role_data._id),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Success");
        navigate(PATH_USER.root);
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  const handleAssignPermission = () => {
    permission_mutation.mutate(selectedVerbData);
  };
  return (
    <>
      <Box sx={{ p: 4, my: 3, mx: 3, background: "white" }}>
        {/* {Object.keys(entryValue).length != 0 && ( */}
        <>
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Role name</InputLabel>
            <InputCustomSize
              placeholder={"Kindly input role name"}
              value={data.name}
              size={"large"}
              onchange={(e: any) =>
                setData((prev) => ({ ...prev, name: e.target.value }))
              }
              type={"text"}
              styles={{ width: isMedium ? "100%" : "50%" }}
            />
          </Stack>
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Role description</InputLabel>
            <InputCustomSize
              placeholder={"Kindly input role description"}
              value={data.description}
              size={"large"}
              onchange={(e: any) =>
                setData((prev) => ({ ...prev, description: e.target.value }))
              }
              type={"text"}
              styles={{ width: isMedium ? "100%" : "50%" }}
            />
          </Stack>
          <LoadingButton
            loading={role_mutation.isPending}
            disabled={!data.name || role_data.show_permission}
            onClick={handleCreateRole}
            sx={{ width: 200, my: 2 }}
            variant="contained"
          >
            Create Role
          </LoadingButton>
        </>
        {/* )} */}
      </Box>
      {role_data.show_permission && (
        <Box
          sx={{
            backgroundColor: "white",
            p: 4,
            m: 3,
          }}
        >
          <Box>
            <Typography sx={{ ...headerSty, my: 0 }} variant="subtitle1">
              Permissions
            </Typography>
            <Typography color={"neutral"} sx={{ my: 0 }} variant="subtitle2">
              Kindly select a permission to assign to role created
            </Typography>
          </Box>
          <Box>
            {loading && filterData.module === "" && (
              <>
                <Skeleton width={200} height={100} />
                <Skeleton width={200} height={100} />
                <Skeleton width={200} height={100} />
                <Skeleton width={200} height={100} />
              </>
            )}
            {stateData?.permissions?.map((val, id) => (
              <Box key={id} sx={{ my: 3 }}>
                <Stack direction="row" alignItems="center">
                  <Button
                    variant="contained"
                    onClick={() => handleModule(val)}
                    sx={{ my: 2, width: 150 }}
                  >
                    {val.toUpperCase()}
                  </Button>
                </Stack>
                {loading && filterData.module === val && (
                  <Skeleton height={100} width={"100%"} />
                )}
                {!loading && filterData.module === val && (
                  <Stack
                    direction={"row"}
                    flexWrap="wrap"
                    alignItems={"center"}
                    spacing={3}
                  >
                    {/* Map over the data to create Material-UI buttons for each verb */}
                    {Object.keys(stateData.module).map((key) => {
                      const entry = stateData.module[key];
                      return (
                        <div key={key} style={{ margin: "4px" }}>
                          {entry.verbs &&
                            Object.keys(entry.verbs).map((verb) => {
                              const verbData = entry.verbs[verb];
                              const description = verbData.descriptions; // Store description

                              return (
                                <Button
                                  key={`${verb}-${description}`} // Unique key using verb and description
                                  onClick={() =>
                                    handleVerbClick(
                                      verb,
                                      description,
                                      verbData,
                                      entry,
                                    )
                                  }
                                  variant="contained"
                                  // Apply unique key check in the button's style logic
                                  style={{
                                    backgroundColor: selectedVerbData.some(
                                      (item) =>
                                        item.privilege.verbs[verb]
                                          ?.descriptions === description,
                                    )
                                      ? "#3b5998"
                                      : "#d3d3d3",
                                    color: selectedVerbData.some(
                                      (item) =>
                                        item.privilege.verbs[verb]
                                          ?.descriptions === description,
                                    )
                                      ? "#fff"
                                      : "#000",
                                    margin: "5px",
                                  }}
                                >
                                  {verb} - {description}
                                </Button>
                              );
                            })}
                        </div>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            ))}
          </Box>
          <Stack>
            <LoadingButton
              loading={role_mutation.isPending}
              disabled={selectedVerbData.length === 0}
              onClick={handleAssignPermission}
              sx={{ width: 200, my: 2 }}
              variant="contained"
            >
              Assign Permissions
            </LoadingButton>
          </Stack>
        </Box>
      )}
    </>
  );
};

export default PermissionsDetailsContent;
